// ─────────────────────────────────────────────────────────────────────────────
// Dashboard → report drill-down links.
//
// Every dashboard number should be verifiable: click it and land on the report
// that produced it, with the SAME employee/manager and the SAME date window
// already applied. This module owns the URL contract so the dashboard tabs and
// the report pages can never drift apart — a link builder here and a matching
// reader (readDrillParams) on the report page.
//
// PARAM CONTRACT (one shared vocabulary across every report):
//   employeeCode  a single employee code to filter to
//   storeCode     a single store code to filter to
//   q             free-text prefill for pages whose only employee filter is a search box
//   manager       a manager's DISPLAY NAME (Mood report filters by name, not code)
//   managerCode   a manager's employee code (pages that filter by code)
//   roleId        role filter (Red Flags page)
//   startDate/endDate   inclusive 'YYYY-MM-DD' window
//   status        'open' = only what still waits for a decision (the two appeals pages:
//                 pending or under review), so a "N waiting" number opens on those N
//
// Dates are always ISO 'YYYY-MM-DD' so they drop straight into <input type="date">
// and into useReportFilters' initializers without reparsing.
// ─────────────────────────────────────────────────────────────────────────────

/** Report routes we deep-link into. Kept here so a route rename breaks one file. */
export const DRILL_ROUTES = {
  dailyAttendance: '/reports/daily-attendance',
  mood: '/reports/mood',
  // Two SEPARATE appeal systems with separate report pages. The Appeals dashboard
  // unions both, so a drill-down must pick the route by the row's flagKind —
  // sending a yellow-flag appeal to the red page would land on a list that
  // structurally cannot contain it.
  appeals: '/reports/appeal-workflow',
  yellowAppeals: '/reports/yellow-flag-appeals',
  // The Red Flags page is ManagerFocusDebugPage — it renders "Red Flags" with
  // Role / Find-employee / From / To, despite the legacy route + file name.
  redFlags: '/settings/focus-debug',
  // Full yellow-flag report (all statuses). Admin-only at the API, so a manager
  // following this link sees the page's own access message rather than data.
  yellowFlags: '/reports/yellow-flags',
} as const;

export interface DrillParams {
  employeeCode?: string | null;
  storeCode?: string | null;
  q?: string | null;
  manager?: string | null;
  managerCode?: string | null;
  roleId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  status?: typeof DRILL_STATUS_OPEN | null;
}

/** The one `status` value: only what still waits for a decision. */
export const DRILL_STATUS_OPEN = 'open';

/** Build a drill-down href. Empty/null values are omitted, so a link never
 *  carries a filter the caller couldn't resolve (which would filter to nothing). */
export const buildDrillUrl = (route: string, params: DrillParams): string => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    const s = v == null ? '' : String(v).trim();
    if (s) qs.set(k, s);
  }
  const q = qs.toString();
  return q ? `${route}?${q}` : route;
};

/** Read the drill-down params on a report page. Returns plain strings ('' when
 *  absent) so they can be fed straight into state initializers. */
export const readDrillParams = (search: string): Required<{ [K in keyof DrillParams]: string }> => {
  const p = new URLSearchParams(search);
  const get = (k: string) => (p.get(k) || '').trim();
  return {
    employeeCode: get('employeeCode'),
    storeCode: get('storeCode'),
    q: get('q'),
    manager: get('manager'),
    managerCode: get('managerCode'),
    roleId: get('roleId'),
    startDate: get('startDate'),
    endDate: get('endDate'),
    status: get('status'),
  };
};

/** The values a link can put in a page's own search box. A page whose only way to
 *  narrow to a person or a store is that box takes the first one present, in this
 *  order (hooks/useLinkedSearch), and takes all three out of the address together. */
export const SEARCH_BOX_PARAMS = ['employeeCode', 'storeCode', 'q'] as const;

/** The text a link asks a page's search box to hold, '' when it asks for none. */
export const searchTextFromLink = (search: string): string => {
  const drill = readDrillParams(search);
  return SEARCH_BOX_PARAMS.map((key) => drill[key]).find(Boolean) ?? '';
};

// ── Date helpers ─────────────────────────────────────────────────────────────

/** Local 'YYYY-MM-DD' (never toISOString — that shifts a day in IST). */
export const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const todayIso = (): string => isoDay(new Date());

/** First + last day of a 'YYYY-MM' month. The END is capped at TODAY for the
 *  current month, so a link never asks a report for future dates. */
export const monthRange = (ym: string): { startDate: string; endDate: string } => {
  const [y, m] = String(ym).slice(0, 7).split('-').map(Number);
  if (!y || !m) { const t = todayIso(); return { startDate: t, endDate: t }; }
  const start = new Date(y, m - 1, 1);
  const end = new Date(y, m, 0); // day 0 of next month = last day of this one
  const today = new Date();
  const capped = end.getTime() > today.getTime() ? today : end;
  return { startDate: isoDay(start), endDate: isoDay(capped) };
};

/** Calendar-year range for a 'YYYY' leaderboard, end-capped at today. */
export const yearRange = (year: string | number): { startDate: string; endDate: string } => {
  const y = Number(year);
  const today = new Date();
  if (!y) return { startDate: isoDay(today), endDate: isoDay(today) };
  const end = new Date(y, 11, 31);
  const capped = end.getTime() > today.getTime() ? today : end;
  return { startDate: `${y}-01-01`, endDate: isoDay(capped) };
};

/**
 * Window for a CURRENT RUN of `days` absent days ending today.
 *
 * IMPORTANT: the run BRIDGES week-offs/holidays without counting them (the
 * engine skips those days rather than breaking the streak), so the calendar span
 * is >= `days`. Walking back exactly `days` would clip the start and hide the
 * first absences. We pad generously (roughly 2 weekend days per working week,
 * plus a couple of days' slack) and let the report show the extra context —
 * showing a few surrounding days is far better than truncating the evidence.
 */
export const absentRunRange = (days: number, anchorIso?: string | null): { startDate: string; endDate: string } => {
  const n = Math.max(1, Math.floor(Number(days) || 1));
  const padded = n + Math.ceil(n / 5) * 2 + 2; // bridge week-offs + slack
  // Anchor on the SERVER's IST "today" when the dashboard supplies it — the browser
  // clock drifts for a non-IST viewer or a tab left open past midnight.
  const [ay, am, ad] = (anchorIso || todayIso()).split('-').map(Number);
  const end = ay && am && ad ? new Date(ay, am - 1, ad) : new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - (padded - 1));
  return { startDate: isoDay(start), endDate: isoDay(end) };
};

/** Month window for a per-month ABSENCE total. Absences only accrue for days strictly
 *  BEFORE today, so the current month ends at yesterday — asking the report for today
 *  (or future days) would show rows the dashboard's count never counted. */
export const absentMonthRange = (ym: string, todayIsoStr?: string | null): { startDate: string; endDate: string } => {
  const { startDate, endDate } = monthRange(ym);
  const today = todayIsoStr || todayIso();
  if (today.slice(0, 7) !== String(ym).slice(0, 7)) return { startDate, endDate };
  const [y, m, d] = today.split('-').map(Number);
  const yest = new Date(y, m - 1, d);
  yest.setDate(yest.getDate() - 1);
  const yestIso = isoDay(yest);
  return { startDate, endDate: yestIso < startDate ? startDate : yestIso };
};
